import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { moduleCanDo } from "../lib/modules";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { tabAccessibleFor } from "../lib/modules";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Trash, Link as LinkIcon, X, Eye, Store } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { DispatchModuleAccessButton } from "../components/DispatchAuditControls";
import { useEditAccessRequest } from "../components/EditAccessRequest";

const EMPTY_CONTACT = { name: "", email: "", mobile: "", contact_type: "Owner", contact_type_other: "" };
const EMPTY_FORM = {
  vendor_name: "",
  is_gst_registered: false,
  vendor_gst: "",
  vendor_pan: "",
  cin_lip_no: "",
  vendor_address: "",
  contacts: [{ ...EMPTY_CONTACT }],
  products_available: [],
  custom_products: [],
  dealing_employees: [],
  vendor_folder_link: "",
  performance_rating: 5,
  has_purchased_before: false,
};

export default function VendorManagement() {
  const { user } = useAuth();
  const canWrite = user?.role === "master_admin" || moduleCanDo(user, "vendor_management", "edit");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inventory] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | active
  // v109.8 — Mandatory reason on edit + delete (Dispatch audit rule)
  const [changeReason, setChangeReason] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [viewMode] = useGlobalViewMode();
  // v109.9.7 — save-and-request when backend blocks vendor edit (403).
  const { dialog: editReqDialog, wrapMutation: wrapEditRequest } = useEditAccessRequest();

  const loadVendors = () => {
    setLoading(true);
    api.get("/vendors")
      .then(({ data }) => setVendors(data || []))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadVendors();
    api.get("/directory").then(({ data }) => setEmployees(data || [])).catch(() => {});
  }, []);

  const inventoryById = useMemo(() => {
    const m = new Map();
    inventory.forEach((i) => m.set(i.id, i));
    return m;
  }, [inventory]);

  const employeesById = useMemo(() => {
    const m = new Map();
    employees.forEach((e) => m.set(e.id, e));
    return m;
  }, [employees]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return vendors.filter((v) => {
      if (statusFilter === "pending" && !v.is_pending) return false;
      if (statusFilter === "active" && v.is_pending) return false;
      if (!q) return true;
      return [
        v.vendor_name, v.vendor_gst, v.vendor_pan, v.cin_lip_no, v.vendor_address,
        ...(v.contacts || []).map((c) => `${c.name} ${c.email} ${c.mobile}`),
      ].some((x) => String(x ?? "").toLowerCase().includes(q));
    });
  }, [vendors, search, statusFilter]);

  const pendingCount = useMemo(() => vendors.filter((v) => v.is_pending).length, [vendors]);

  const viewVendor = viewId ? vendors.find((v) => v.id === viewId) : null;

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, contacts: [{ ...EMPTY_CONTACT }] });
    setChangeReason("");
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setEditingId(v.id);
    setChangeReason("");
    setForm({
      vendor_name: v.vendor_name || "",
      is_gst_registered: !!v.is_gst_registered,
      vendor_gst: v.vendor_gst || "",
      vendor_pan: v.vendor_pan || "",
      cin_lip_no: v.cin_lip_no || "",
      vendor_address: v.vendor_address || "",
      contacts: (v.contacts && v.contacts.length > 0) ? v.contacts.map((c) => ({
        name: c.name || "", email: c.email || "", mobile: c.mobile || "",
        contact_type: c.contact_type || "Other", contact_type_other: c.contact_type_other || "",
      })) : [{ ...EMPTY_CONTACT }],
      products_available: v.products_available || [],
      custom_products: v.custom_products || [],
      dealing_employees: v.dealing_employees || [],
      vendor_folder_link: v.vendor_folder_link || "",
      performance_rating: v.performance_rating || 5,
      has_purchased_before: !!v.has_purchased_before,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.vendor_name.trim()) { toast.error("Vendor Name is required"); return; }
    if (form.is_gst_registered && !form.vendor_gst.trim()) {
      toast.error("Vendor GST is required when GST-registered"); return;
    }
    if (!form.is_gst_registered && !form.vendor_pan.trim()) {
      toast.error("Vendor PAN is required when not GST-registered"); return;
    }
    setSaving(true);
    try {
      // Strip empty contacts before send
      const payload = {
        ...form,
        contacts: (form.contacts || []).filter((c) => (c.name || c.email || c.mobile)),
      };
      if (editingId) {
        if (!changeReason.trim() || changeReason.trim().length < 3) {
          toast.error("Please state a reason for this change (min 3 chars)");
          setSaving(false);
          return;
        }
        const finalPayload = { ...payload, change_reason: changeReason.trim() };
        // v109.9.7 — auto-stage via approval request if backend returns 403.
        const result = await wrapEditRequest(
          () => api.patch(`/vendors/${editingId}`, finalPayload),
          {
            moduleKey: "vendor_management",
            recordId: editingId,
            recordLabel: form.vendor_name || editingId,
            proposedPayload: finalPayload,
            stageOn403: true,
          },
        );
        if (!result) {
          setDialogOpen(false);
          setChangeReason("");
          setSaving(false);
          return;
        }
        toast.success("Vendor updated");
        setChangeReason("");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor created");
      }
      setDialogOpen(false);
      loadVendors();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    if (!deleteReason.trim() || deleteReason.trim().length < 3) {
      toast.error("Please state a reason for deletion (min 3 chars)");
      return;
    }
    try {
      await api.delete(`/vendors/${deleteId}`, { data: { delete_reason: deleteReason.trim() } });
      toast.success("Vendor moved to Recycle Bin (purged after 30 days)");
      setDeleteId(null);
      setDeleteReason("");
      loadVendors();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const approveVendor = async (vid) => {
    try {
      await api.post(`/vendors/${vid}/approve`);
      toast.success("Vendor approved");
      loadVendors();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="max-w-[1400px] space-y-6">
      <PageHeader
        moduleKey="vendor_management"
        tone="indigo"
        icon={Store}
        eyebrow="Workspace"
        title="Vendor Management"
        subtitle={canWrite ? "Maintain the master vendor list — GST/PAN, contacts, dealing employees, product lines and performance." : "Read-only access. Master Admin maintains this list."}
        actions={
          <div className="flex items-center gap-2">
            <DispatchModuleAccessButton moduleKey="vendor_management" label="Vendor Management" />
            {canWrite && (
              <Button onClick={openNew} data-testid="vendor-add-btn">
                <Plus size={14} className="mr-1" /> Add Vendor
              </Button>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor, GST, PAN, contact…"
            className="pl-9 h-10"
            data-testid="vendor-search"
          />
        </div>
        {/* Status filter via Shadcn Tabs */}
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-auto">
          <TabsList>
            {tabAccessibleFor(user, "vendor_management", "all") && (
              <TabsTrigger value="all" data-testid="vendor-filter-all">All</TabsTrigger>
            )}
            {tabAccessibleFor(user, "vendor_management", "pending") && (
              <TabsTrigger value="pending" data-testid="vendor-filter-pending">Pending · {pendingCount}</TabsTrigger>
            )}
            {tabAccessibleFor(user, "vendor_management", "active") && (
              <TabsTrigger value="active" data-testid="vendor-filter-active">Active</TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        <Badge variant="outline" className="ml-auto text-[11px]" data-testid="vendor-count">
          {filtered.length} of {vendors.length}
        </Badge>
      </div>

      {loading ? (
        <div className="pk-card p-12 text-center text-slate-400">Loading vendors…</div>
      ) : filtered.length === 0 ? (
        <div className="pk-card p-12 text-center text-slate-400" data-testid="vendor-empty">
          {vendors.length === 0 ? "No vendors yet — click Add Vendor to create one." : "No vendors match your search."}
        </div>
      ) : (
        <div className="pk-card overflow-hidden">
          <div className="overflow-x-auto" style={viewMode === "card" ? { display: "none" } : undefined}>
            <table className="w-full text-sm responsive-table" data-testid="vendor-table">
              <thead className="bg-slate-50/80 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Vendor</th>
                  <th className="px-4 py-3 font-semibold">GST / PAN</th>
                  <th className="px-4 py-3 font-semibold">Primary Contact</th>
                  <th className="px-4 py-3 font-semibold">Products</th>
                  <th className="px-4 py-3 font-semibold">Dealing With</th>
                  <th className="px-4 py-3 font-semibold">Rating</th>
                  <th className="px-4 py-3 font-semibold">Purchased</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const primary = (v.contacts || [])[0];
                  return (
                    <tr key={v.id} className="border-b last:border-0 hover:bg-slate-50/60" data-testid={`vendor-row-${v.id}`}>
                      <td data-label="Vendor" className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            type="button"
                            onClick={() => setViewId(v.id)}
                            className="font-semibold text-slate-900 hover:text-violet-700 text-left"
                            data-testid={`vendor-name-${v.id}`}
                          >
                            {v.vendor_name}
                          </button>
                          {v.is_pending && (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[9px]">PENDING</Badge>
                          )}
                        </div>
                        {v.cin_lip_no && (
                          <div className="text-[11px] text-slate-500 mono">CIN/LLP · {v.cin_lip_no}</div>
                        )}
                      </td>
                      <td data-label="GST / PAN" className="px-4 py-3">
                        {v.is_gst_registered ? (
                          <div>
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">GST</Badge>
                            <div className="mono text-[11px] text-slate-700 mt-1">{v.vendor_gst || "—"}</div>
                          </div>
                        ) : (
                          <div>
                            <Badge variant="outline" className="text-slate-700">PAN</Badge>
                            <div className="mono text-[11px] text-slate-700 mt-1">{v.vendor_pan || "—"}</div>
                          </div>
                        )}
                      </td>
                      <td data-label="Primary Contact" className="px-4 py-3">
                        {primary ? (
                          <div className="text-[12px]">
                            <div className="font-medium text-slate-800">{primary.name || "—"}</div>
                            <div className="text-slate-500">{primary.email}</div>
                            <div className="text-slate-500 mono">{primary.mobile}</div>
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td data-label="Products" className="px-4 py-3 text-[12px]">
                        {(() => {
                          const invNames = (v.products_available || [])
                            .map((pid) => inventoryById.get(pid)?.product_name)
                            .filter(Boolean);
                          const customNames = v.custom_products || [];
                          const all = [...invNames, ...customNames];
                          if (all.length === 0) return <span className="text-slate-400">—</span>;
                          return (
                            <div className="flex flex-wrap gap-1 max-w-[260px]">
                              {invNames.map((name, i) => (
                                <span
                                  key={`inv-${i}`}
                                  className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-violet-100 text-violet-800 font-medium"
                                >
                                  {name}
                                </span>
                              ))}
                              {customNames.map((name, i) => (
                                <span
                                  key={`c-${i}`}
                                  className="inline-block px-1.5 py-0.5 rounded text-[10px] border border-dashed border-slate-300 text-slate-700 font-medium"
                                >
                                  {name}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td data-label="Dealing With" className="px-4 py-3 text-[12px]">
                        {v.dealing_employees?.length > 0 ? (
                          <span className="text-slate-700">
                            {(v.dealing_employees || []).slice(0, 2).map((id) => employeesById.get(id)?.name || "—").join(", ")}
                            {v.dealing_employees.length > 2 && ` +${v.dealing_employees.length - 2}`}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td data-label="Rating" className="px-4 py-3">
                        <RatingBar value={v.performance_rating || 0} />
                      </td>
                      <td data-label="Purchased" className="px-4 py-3">
                        {v.has_purchased_before ? (
                          <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">Yes</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-600">No</Badge>
                        )}
                      </td>
                      <td data-label="Actions" className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {v.is_pending && canWrite && (
                            <Button
                              size="sm"
                              onClick={() => approveVendor(v.id)}
                              className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`vendor-approve-${v.id}`}
                            >Approve</Button>
                          )}
                          <button
                            onClick={() => setViewId(v.id)}
                            className="h-8 w-8 inline-flex items-center justify-center text-slate-600 hover:text-violet-700 rounded hover:bg-slate-100"
                            data-testid={`vendor-view-${v.id}`}
                            title="View Details"
                          >
                            <Eye size={14} />
                          </button>
                          {v.vendor_folder_link && (
                            <a
                              href={v.vendor_folder_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-8 w-8 inline-flex items-center justify-center text-slate-600 hover:text-violet-700 rounded hover:bg-slate-100"
                              data-testid={`vendor-link-${v.id}`}
                              title="Vendor Folder"
                            >
                              <LinkIcon size={14} />
                            </a>
                          )}
                          {canWrite && (
                            <>
                              <button
                                onClick={() => openEdit(v)}
                                className="h-8 w-8 inline-flex items-center justify-center text-slate-600 hover:text-violet-700 rounded hover:bg-slate-100"
                                data-testid={`vendor-edit-${v.id}`}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => setDeleteId(v.id)}
                                className="h-8 w-8 inline-flex items-center justify-center text-rose-600 hover:text-rose-700 rounded hover:bg-rose-50"
                                data-testid={`vendor-delete-${v.id}`}
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* v109.9 — Card view */}
          {viewMode === "card" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="vendor-card-grid">
              {filtered.map((v) => {
                const primary = (v.contacts || [])[0];
                const invNames = (v.products_available || [])
                  .map((pid) => inventoryById.get(pid)?.product_name)
                  .filter(Boolean);
                const customNames = v.custom_products || [];
                const allProducts = [...invNames, ...customNames];
                const dealingNames = (v.dealing_employees || [])
                  .map((eid) => employeesById.get(eid)?.name)
                  .filter(Boolean);
                return (
                <div key={v.id} className="relative rounded-xl border border-slate-200 bg-white hover:shadow-lg transition-shadow p-3.5" data-testid={`vendor-card-${v.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={() => setViewId(v.id)}
                        className="font-bold text-slate-900 text-[13.5px] truncate text-left hover:text-violet-700 block w-full"
                        data-testid={`vendor-card-name-${v.id}`}
                      >
                        {v.vendor_name || "—"}
                      </button>
                      <div className="text-[10.5px] text-slate-500 mono truncate mt-0.5">
                        {v.is_gst_registered ? `GST ${v.vendor_gst || "—"}` : `PAN ${v.vendor_pan || "—"}`}
                      </div>
                      {v.cin_lip_no && (
                        <div className="text-[10px] text-slate-400 mono truncate">CIN/LLP · {v.cin_lip_no}</div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {v.is_pending
                        ? <Badge className="bg-amber-100 text-amber-800 text-[9px]">PENDING</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-800 text-[9px]">Active</Badge>}
                      {v.has_purchased_before && (
                        <Badge className="bg-violet-100 text-violet-800 text-[9px]">Purchased</Badge>
                      )}
                    </div>
                  </div>
                  {primary && (
                    <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1.5 mb-2 text-[11px]" data-testid={`vendor-card-contact-${v.id}`}>
                      <div className="font-semibold text-slate-800 truncate">{primary.name || "—"}</div>
                      <div className="text-slate-500 truncate">{primary.email || "—"}</div>
                      <div className="text-slate-500 mono truncate">{primary.mobile || "—"}</div>
                    </div>
                  )}
                  {allProducts.length > 0 && (
                    <div className="mb-2">
                      <div className="text-[9.5px] uppercase tracking-wider font-bold text-slate-400 mb-1">Products ({allProducts.length})</div>
                      <div className="flex flex-wrap gap-1">
                        {invNames.slice(0, 3).map((n, i) => (
                          <span key={`i-${i}`} className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-violet-100 text-violet-800 font-medium truncate max-w-[110px]">{n}</span>
                        ))}
                        {customNames.slice(0, Math.max(0, 3 - invNames.length)).map((n, i) => (
                          <span key={`c-${i}`} className="inline-block px-1.5 py-0.5 rounded text-[10px] border border-dashed border-slate-300 text-slate-700 font-medium truncate max-w-[110px]">{n}</span>
                        ))}
                        {allProducts.length > 3 && (
                          <span className="text-[10px] text-slate-500 px-1">+{allProducts.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}
                  {dealingNames.length > 0 && (
                    <div className="text-[11px] text-slate-600 truncate mb-2">
                      <span className="text-slate-400">Dealing With: </span>
                      <b>{dealingNames.slice(0, 2).join(", ")}</b>
                      {dealingNames.length > 2 && ` +${dealingNames.length - 2}`}
                    </div>
                  )}
                  <div className="mb-2">
                    <div className="flex items-center gap-1.5 text-[10.5px]">
                      <span className="text-slate-400">Rating</span>
                      <RatingBar value={v.performance_rating || 0} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 pt-2 border-t border-slate-100 flex-wrap">
                    <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setViewId(v.id)} data-testid={`vendor-card-view-${v.id}`}>
                      <Eye size={11} className="mr-1" /> View
                    </Button>
                    {v.vendor_folder_link && (
                      <a
                        href={v.vendor_folder_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-7 px-2 inline-flex items-center gap-1 text-[10px] text-slate-600 border border-slate-200 rounded hover:bg-slate-50"
                        data-testid={`vendor-card-link-${v.id}`}
                        title="Vendor Folder"
                      >
                        <LinkIcon size={11} /> Folder
                      </a>
                    )}
                    {v.is_pending && canWrite && (
                      <Button size="sm" onClick={() => approveVendor(v.id)} className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" data-testid={`vendor-card-approve-${v.id}`}>
                        Approve
                      </Button>
                    )}
                    {canWrite && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => openEdit(v)} data-testid={`vendor-card-edit-${v.id}`}>
                          <Pencil size={11} />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[10px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setDeleteId(v.id)} data-testid={`vendor-card-delete-${v.id}`}>
                          <Trash2 size={11} />
                        </Button>
                      </>
                    )}
                    {!canWrite && (
                      <Badge className="bg-slate-100 text-slate-500 text-[9.5px] ring-1 ring-slate-200 h-6 flex items-center px-2 ml-auto">View only</Badge>
                    )}
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Vendor Name <span className="text-rose-500">*</span></Label>
                <Input
                  value={form.vendor_name}
                  onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                  className="mt-1.5"
                  data-testid="vendor-form-name"
                />
              </div>

              <div className="md:col-span-2 flex items-center gap-3 p-3 bg-slate-50 rounded-md border">
                <Label className="text-[12px] font-semibold text-slate-700 flex-1">Is Vendor GST Registered?</Label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_gst_registered: true })}
                    className={`px-3 py-1 text-[11px] font-semibold rounded-full border ${form.is_gst_registered ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-600"}`}
                    data-testid="vendor-gst-yes"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_gst_registered: false })}
                    className={`px-3 py-1 text-[11px] font-semibold rounded-full border ${!form.is_gst_registered ? "bg-rose-100 border-rose-300 text-rose-800" : "bg-white border-slate-200 text-slate-600"}`}
                    data-testid="vendor-gst-no"
                  >
                    No
                  </button>
                </div>
              </div>

              {form.is_gst_registered ? (
                <div className="md:col-span-2">
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Vendor GST <span className="text-rose-500">*</span></Label>
                  <Input
                    value={form.vendor_gst}
                    onChange={(e) => setForm({ ...form, vendor_gst: e.target.value.toUpperCase() })}
                    placeholder="22AAAAA0000A1Z5"
                    className="mt-1.5 mono"
                    data-testid="vendor-form-gst"
                  />
                </div>
              ) : (
                <div className="md:col-span-2">
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Vendor PAN <span className="text-rose-500">*</span></Label>
                  <Input
                    value={form.vendor_pan}
                    onChange={(e) => setForm({ ...form, vendor_pan: e.target.value.toUpperCase() })}
                    placeholder="AAAAA0000A"
                    className="mt-1.5 mono"
                    data-testid="vendor-form-pan"
                  />
                </div>
              )}

              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">CIN / LLP No.</Label>
                <Input
                  value={form.cin_lip_no}
                  onChange={(e) => setForm({ ...form, cin_lip_no: e.target.value.toUpperCase() })}
                  className="mt-1.5 mono"
                  data-testid="vendor-form-cin"
                />
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Vendor Folder Link</Label>
                <Input
                  value={form.vendor_folder_link}
                  onChange={(e) => setForm({ ...form, vendor_folder_link: e.target.value })}
                  placeholder="https://drive.google.com/…"
                  className="mt-1.5"
                  data-testid="vendor-form-folder"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Vendor Address</Label>
                <Textarea
                  value={form.vendor_address}
                  onChange={(e) => setForm({ ...form, vendor_address: e.target.value })}
                  className="mt-1.5"
                  rows={2}
                  data-testid="vendor-form-address"
                />
              </div>
            </div>

            {/* Contacts repeater */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Contacts</Label>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setForm({ ...form, contacts: [...form.contacts, { ...EMPTY_CONTACT }] })}
                  data-testid="vendor-contact-add"
                >
                  <Plus size={12} className="mr-1" /> Add Contact
                </Button>
              </div>
              <div className="space-y-3">
                {form.contacts.map((c, idx) => (
                  <div key={idx} className="p-3 rounded-md border bg-slate-50/50" data-testid={`vendor-contact-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Contact #{idx + 1}</span>
                      {form.contacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, contacts: form.contacts.filter((_, i) => i !== idx) })}
                          className="text-rose-600 hover:text-rose-700"
                          data-testid={`vendor-contact-remove-${idx}`}
                        >
                          <Trash size={12} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input
                        placeholder="Contact Name"
                        value={c.name}
                        onChange={(e) => {
                          const next = [...form.contacts]; next[idx] = { ...c, name: e.target.value };
                          setForm({ ...form, contacts: next });
                        }}
                        data-testid={`vendor-contact-name-${idx}`}
                      />
                      <Input
                        placeholder="Contact Email"
                        type="email"
                        value={c.email}
                        onChange={(e) => {
                          const next = [...form.contacts]; next[idx] = { ...c, email: e.target.value };
                          setForm({ ...form, contacts: next });
                        }}
                        data-testid={`vendor-contact-email-${idx}`}
                      />
                      <Input
                        placeholder="Contact Number"
                        value={c.mobile}
                        onChange={(e) => {
                          const next = [...form.contacts]; next[idx] = { ...c, mobile: e.target.value };
                          setForm({ ...form, contacts: next });
                        }}
                        data-testid={`vendor-contact-mobile-${idx}`}
                      />
                      <Select
                        value={c.contact_type}
                        onValueChange={(v) => {
                          const next = [...form.contacts]; next[idx] = { ...c, contact_type: v };
                          setForm({ ...form, contacts: next });
                        }}
                      >
                        <SelectTrigger data-testid={`vendor-contact-type-${idx}`}>
                          <SelectValue placeholder="Contact Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Owner">Owner</SelectItem>
                          <SelectItem value="Employee">Employee</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {c.contact_type === "Other" && (
                        <Input
                          placeholder="Specify type"
                          className="md:col-span-2"
                          value={c.contact_type_other}
                          onChange={(e) => {
                            const next = [...form.contacts]; next[idx] = { ...c, contact_type_other: e.target.value };
                            setForm({ ...form, contacts: next });
                          }}
                          data-testid={`vendor-contact-type-other-${idx}`}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product Available — unified typeahead: type any product name; matches against
                Inventory are shown as suggestions, anything else can be added as a custom entry. */}
            <ProductsCombobox
              inventory={inventory}
              selectedInventoryIds={form.products_available}
              customProducts={form.custom_products}
              onChangeInventoryIds={(arr) => setForm({ ...form, products_available: arr })}
              onChangeCustom={(arr) => setForm({ ...form, custom_products: arr })}
            />

            {/* Dealing With (multi-select from employees) */}
            <MultiSelectPicker
              label="Who Is Dealing With This Vendor"
              options={employees.map((e) => ({
                value: e.id,
                label: `${e.name}${e.role ? ` · ${e.role.replace(/_/g, " ")}` : ""}`,
              }))}
              selected={form.dealing_employees}
              onChange={(arr) => setForm({ ...form, dealing_employees: arr })}
              testid="vendor-form-dealing"
              emptyHint="No employees available."
            />

            {/* Performance Rating slider */}
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 flex items-center justify-between">
                <span>Vendor Performance (1 = poor, 10 = excellent)</span>
                <span className="mono text-violet-700 font-bold text-base">{form.performance_rating}/10</span>
              </Label>
              <input
                type="range"
                min={1}
                max={10}
                step={1}
                value={form.performance_rating}
                onChange={(e) => setForm({ ...form, performance_rating: parseInt(e.target.value, 10) })}
                className="w-full mt-2 accent-violet-600"
                data-testid="vendor-form-rating"
              />
              <RatingBar value={form.performance_rating} />
            </div>

            {/* Purchased flag */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md border">
              <Label className="text-[12px] font-semibold text-slate-700 flex-1">Did we ever buy something from this Vendor?</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, has_purchased_before: true })}
                  className={`px-3 py-1 text-[11px] font-semibold rounded-full border ${form.has_purchased_before ? "bg-violet-100 border-violet-300 text-violet-800" : "bg-white border-slate-200 text-slate-600"}`}
                  data-testid="vendor-purchased-yes"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, has_purchased_before: false })}
                  className={`px-3 py-1 text-[11px] font-semibold rounded-full border ${!form.has_purchased_before ? "bg-slate-200 border-slate-300 text-slate-700" : "bg-white border-slate-200 text-slate-600"}`}
                  data-testid="vendor-purchased-no"
                >
                  No
                </button>
              </div>
            </div>
          </div>

          {editingId && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-rose-700 mb-1">
                Reason for this change <span className="text-rose-500">*</span>
              </Label>
              <Textarea
                rows={2}
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value.slice(0, 500))}
                placeholder="e.g. Updated GST number, corrected address, new contact added…"
                className="text-[12.5px]"
                data-testid="vendor-change-reason"
              />
              <div className="text-[10px] text-slate-400 flex justify-between mt-1">
                <span>{changeReason.trim().length < 3 ? "Enter at least 3 characters (recorded in audit log)" : "Looks good — will be recorded in the audit log"}</span>
                <span>{changeReason.length}/500</span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || (editingId && changeReason.trim().length < 3)}
              data-testid="vendor-form-save"
            >
              {saving ? "Saving…" : (editingId ? "Update Vendor" : "Create Vendor")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Details Drawer */}
      <Dialog open={!!viewVendor} onOpenChange={(o) => !o && setViewId(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {viewVendor && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{viewVendor.vendor_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <DetailRow label={viewVendor.is_gst_registered ? "GST Number" : "PAN Number"} value={viewVendor.is_gst_registered ? viewVendor.vendor_gst : viewVendor.vendor_pan} mono />
                  <DetailRow label="CIN / LLP" value={viewVendor.cin_lip_no || "—"} mono />
                  <DetailRow label="Performance" value={
                    <div className="flex items-center gap-2">
                      <span className="mono font-bold text-violet-700">{viewVendor.performance_rating || 0}/10</span>
                      <RatingBar value={viewVendor.performance_rating || 0} />
                    </div>
                  } />
                  <DetailRow label="Ever Purchased" value={viewVendor.has_purchased_before ? "Yes" : "No"} />
                </div>
                {viewVendor.vendor_address && (
                  <DetailRow label="Address" value={viewVendor.vendor_address} />
                )}
                {viewVendor.vendor_folder_link && (
                  <DetailRow label="Vendor Folder" value={
                    <a href={viewVendor.vendor_folder_link} target="_blank" rel="noopener noreferrer" className="text-violet-700 hover:underline break-all">{viewVendor.vendor_folder_link}</a>
                  } />
                )}

                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Contacts ({viewVendor.contacts?.length || 0})</div>
                  {(viewVendor.contacts || []).length === 0 ? (
                    <div className="text-slate-400 italic text-[12px]">No contacts</div>
                  ) : (
                    <div className="space-y-2">
                      {viewVendor.contacts.map((c, i) => (
                        <div key={i} className="p-2.5 bg-slate-50 rounded border text-[12px]">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-slate-800">{c.name || "—"}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {c.contact_type === "Other" ? (c.contact_type_other || "Other") : c.contact_type}
                            </Badge>
                          </div>
                          <div className="text-slate-600">{c.email}</div>
                          <div className="text-slate-600 mono">{c.mobile}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  {(() => {
                    const totalCount = (viewVendor.products_available?.length || 0) + (viewVendor.custom_products?.length || 0);
                    return (
                      <>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                          Products Available ({totalCount})
                        </div>
                        {totalCount === 0 ? (
                          <div className="text-slate-400 italic text-[12px]">None</div>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {(viewVendor.products_available || []).map((pid) => {
                              const item = inventoryById.get(pid);
                              return (
                                <Badge key={`inv-${pid}`} className="text-[11px] bg-violet-100 text-violet-800 hover:bg-violet-100">
                                  {item ? `${item.product_name}${item.sku ? ` · ${item.sku}` : ""}` : pid}
                                </Badge>
                              );
                            })}
                            {(viewVendor.custom_products || []).map((name, i) => (
                              <Badge key={`c-${i}`} variant="outline" className="text-[11px] border-dashed text-slate-700">
                                {name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Dealing With ({viewVendor.dealing_employees?.length || 0})</div>
                  {(viewVendor.dealing_employees || []).length === 0 ? (
                    <div className="text-slate-400 italic text-[12px]">None</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {viewVendor.dealing_employees.map((eid) => {
                        const emp = employeesById.get(eid);
                        return (
                          <Badge key={eid} variant="outline" className="text-[11px]">
                            {emp ? emp.name : eid}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => { if (!o) { setDeleteId(null); setDeleteReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move vendor to Recycle Bin?</AlertDialogTitle>
            <AlertDialogDescription>
              The vendor will be soft-deleted and <b>permanently purged after 30 days</b>. Master Admin can restore it from the Dispatch → Logs tab during that window.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-3">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-1">Reason (mandatory)</Label>
            <Textarea
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Duplicate vendor / no longer trading / merged into another entry"
              className="text-[12.5px]"
              data-testid="vendor-delete-reason"
              autoFocus
            />
            <div className="text-[10px] text-slate-400 flex justify-between mt-1">
              <span>{deleteReason.trim().length < 3 ? "Enter at least 3 characters" : "Looks good"}</span>
              <span>{deleteReason.length}/500</span>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteReason.trim().length < 3}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="vendor-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* v109.9.7 — Save-and-request dialog (auto-opens on 403) */}
      {editReqDialog}
    </div>
  );
}

function RatingBar({ value }) {
  const v = Math.max(0, Math.min(10, Number(value) || 0));
  const color =
    v >= 8 ? "bg-emerald-500" :
    v >= 5 ? "bg-amber-500" :
    v > 0 ? "bg-rose-500" : "bg-slate-300";
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 w-20 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${(v / 10) * 100}%` }} />
      </div>
      <span className="text-[11px] mono text-slate-600">{v}/10</span>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`text-slate-800 ${mono ? "mono" : ""} mt-0.5`}>{value}</div>
    </div>
  );
}

function ProductsCombobox({ inventory, selectedInventoryIds, customProducts, onChangeInventoryIds, onChangeCustom }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selectedInvSet = new Set(selectedInventoryIds || []);
  const customLowerSet = new Set((customProducts || []).map((p) => p.toLowerCase()));
  const totalCount = (selectedInventoryIds?.length || 0) + (customProducts?.length || 0);

  // Inventory suggestions matching the query (skip already-selected ones; cap at 8).
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return inventory
      .filter((i) => !selectedInvSet.has(i.id))
      .filter((i) => `${i.product_name} ${i.sku || ""}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [inventory, query, selectedInventoryIds]);

  const trimmed = query.trim();
  // Show "Add as custom" affordance when query is non-empty AND not already matching an existing
  // inventory item exactly AND not already in custom list.
  const exactInventoryMatch = trimmed
    ? inventory.find((i) => i.product_name.toLowerCase() === trimmed.toLowerCase())
    : null;
  const canAddCustom = trimmed.length > 0
    && !exactInventoryMatch
    && !customLowerSet.has(trimmed.toLowerCase());

  const pickInventory = (item) => {
    onChangeInventoryIds([...(selectedInventoryIds || []), item.id]);
    setQuery("");
    setOpen(true);  // keep open so user can add more
  };

  const addCustom = () => {
    if (!canAddCustom) return;
    onChangeCustom([...(customProducts || []), trimmed]);
    setQuery("");
    setOpen(true);
  };

  const removeInventory = (id) => {
    onChangeInventoryIds((selectedInventoryIds || []).filter((x) => x !== id));
  };

  const removeCustom = (idx) => {
    const next = [...(customProducts || [])];
    next.splice(idx, 1);
    onChangeCustom(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
          Products Available
        </Label>
        {totalCount > 0 && (
          <span className="text-[10px] mono text-slate-500">{totalCount} selected</span>
        )}
      </div>
      <div className="text-[11px] text-slate-500 mb-1.5">
        Start typing to search Inventory — pick a match, or press <kbd className="px-1 py-0.5 mono text-[10px] bg-slate-100 border rounded">Enter</kbd> to add a custom product.
      </div>

      <div className="relative">
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (suggestions.length > 0) {
                pickInventory(suggestions[0]);
              } else if (canAddCustom) {
                addCustom();
              }
            }
          }}
          placeholder="Type a product name…"
          className="h-10"
          data-testid="vendor-form-products-input"
        />

        {open && (suggestions.length > 0 || canAddCustom) && (
          <div
            className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
            data-testid="vendor-form-products-suggestions"
          >
            {suggestions.length > 0 && (
              <div>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60">
                  From Inventory
                </div>
                {suggestions.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickInventory(item)}
                    className="w-full px-3 py-2 text-left text-[12px] hover:bg-violet-50 flex items-center justify-between gap-2 border-b last:border-0"
                    data-testid={`vendor-form-products-inv-${item.id}`}
                  >
                    <span className="text-slate-800 font-medium truncate">{item.product_name}</span>
                    {item.sku && <span className="mono text-[10px] text-slate-500 flex-shrink-0">{item.sku}</span>}
                  </button>
                ))}
              </div>
            )}
            {canAddCustom && (
              <div>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60">
                  Custom Product
                </div>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={addCustom}
                  className="w-full px-3 py-2 text-left text-[12px] hover:bg-emerald-50 flex items-center gap-2"
                  data-testid="vendor-form-products-add-custom"
                >
                  <Plus size={12} className="text-emerald-600" />
                  <span className="text-slate-800">Add as custom: <span className="font-semibold">"{trimmed}"</span></span>
                </button>
              </div>
            )}
            {suggestions.length === 0 && !canAddCustom && trimmed && (
              <div className="px-3 py-3 text-[12px] text-slate-400 italic">
                "{trimmed}" is already added.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected chips */}
      {totalCount > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="vendor-form-products-chips">
          {(selectedInventoryIds || []).map((id) => {
            const item = inventory.find((i) => i.id === id);
            return (
              <Badge
                key={`inv-${id}`}
                className="text-[11px] bg-violet-100 text-violet-800 hover:bg-violet-100 gap-1.5"
                data-testid={`vendor-form-products-chip-inv-${id}`}
              >
                <span>{item ? item.product_name : id}</span>
                {item?.sku && <span className="mono text-[9px] opacity-70">{item.sku}</span>}
                <button
                  type="button"
                  onClick={() => removeInventory(id)}
                  className="text-violet-700 hover:text-violet-900"
                  data-testid={`vendor-form-products-rm-inv-${id}`}
                >
                  <X size={11} />
                </button>
              </Badge>
            );
          })}
          {(customProducts || []).map((name, idx) => (
            <Badge
              key={`c-${idx}`}
              variant="outline"
              className="text-[11px] border-dashed gap-1.5"
              data-testid={`vendor-form-products-chip-custom-${idx}`}
            >
              <span>{name}</span>
              <span className="text-[8px] uppercase tracking-wider opacity-60 font-bold">Custom</span>
              <button
                type="button"
                onClick={() => removeCustom(idx)}
                className="text-slate-500 hover:text-rose-600"
                data-testid={`vendor-form-products-rm-custom-${idx}`}
              >
                <X size={11} />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiSelectPicker({ label, options, selected, onChange, testid, emptyHint }) {
  const selectedSet = new Set(selected || []);
  const toggle = (val) => {
    const next = new Set(selectedSet);
    if (next.has(val)) next.delete(val); else next.add(val);
    onChange(Array.from(next));
  };
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="mt-1.5 w-full h-10 px-3 text-sm rounded-md border bg-white border-slate-200 hover:bg-slate-50 text-left flex items-center justify-between"
            data-testid={testid}
          >
            <span className="text-slate-700">
              {selected?.length > 0 ? `${selected.length} selected` : <span className="text-slate-400">Click to select…</span>}
            </span>
            <span className="text-slate-400 text-xs">▼</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[min(28rem,90vw)] max-h-80 overflow-y-auto" align="start">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
            <span>{label}</span>
            {selected?.length > 0 && (
              <button
                onClick={(e) => { e.preventDefault(); onChange([]); }}
                className="text-[10px] font-semibold text-rose-600 hover:underline"
              >
                Clear
              </button>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.length === 0 ? (
            <div className="px-3 py-3 text-[12px] text-slate-400 italic">{emptyHint || "No options"}</div>
          ) : options.map((opt) => (
            <DropdownMenuCheckboxItem
              key={opt.value}
              checked={selectedSet.has(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
              onSelect={(e) => e.preventDefault()}
              className="text-[12px]"
              data-testid={`${testid}-opt-${opt.value}`}
            >
              {opt.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {selected?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {selected.map((v) => {
            const opt = options.find((o) => o.value === v);
            return (
              <Badge key={v} variant="outline" className="text-[10px] gap-1">
                {opt?.label || v}
                <button
                  type="button"
                  onClick={() => toggle(v)}
                  className="text-slate-400 hover:text-rose-600"
                  data-testid={`${testid}-rm-${v}`}
                >
                  <X size={10} />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
